/**
 * Vote split (People tab), Apache ECharts 6.
 * Initialised ONCE on mount from the already-hydrated store (so it paints with real data, no
 * spinner), then updated in place with partial setOption when an `outcome.set` delta arrives.
 */
import { useEffect, useRef } from "react";
import { useThemeVersion } from "../../theme/runtime";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { meetingStore } from "../../stores/meetingStore";
import { useStoreEffect } from "../../hooks/useStoreEffect";
import { prefersReducedMotion, recipes } from "../../motion";

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const cssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function VoteSplitChart({ outcomeId }: { outcomeId: string }) {
  const elRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const themeVersion = useThemeVersion();   // re-read the colors when the dashboard theme changes

  useEffect(() => {
    const el = elRef.current!;
    const chart = echarts.init(el, null, { renderer: "canvas" });
    chartRef.current = chart;
    const o = meetingStore.getState().outcomes.find((x) => x.id === outcomeId);
    chart.setOption({
      animation: !prefersReducedMotion(),
      animationDurationUpdate: recipes.liveValueChange.duration,
      grid: { left: 0, right: 0, top: 0, bottom: 0 },
      xAxis: { type: "value", show: false, max: (o?.yes ?? 0) + (o?.no ?? 0) || 1 },
      yAxis: { type: "category", show: false, data: ["vote"] },
      tooltip: { trigger: "item" },
      series: [
        { id: "yes", name: "Yes", type: "bar", stack: "v", barWidth: 6, data: [o?.yes ?? 0], itemStyle: { color: cssVar("--positive"), borderRadius: 3, borderColor: cssVar("--bg-surface"), borderWidth: 1.5 } },
        { id: "no", name: "No", type: "bar", stack: "v", barWidth: 6, data: [o?.no ?? 0], itemStyle: { color: cssVar("--negative"), borderRadius: 3, borderColor: cssVar("--bg-surface"), borderWidth: 1.5 } },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);
    return () => { ro.disconnect(); chart.dispose(); chartRef.current = null; };
  }, [outcomeId, themeVersion]);

  // Live updates: merge by series id, no re-init, no re-render.
  useStoreEffect(
    meetingStore,
    (s) => s.outcomes.find((x) => x.id === outcomeId),
    (o) => {
      if (!o || !chartRef.current) return;
      chartRef.current.setOption({ xAxis: { max: o.yes + o.no || 1 }, series: [{ id: "yes", data: [o.yes] }, { id: "no", data: [o.no] }] }, { lazyUpdate: true });
    },
    { fireImmediately: false },
  );

  return <div ref={elRef} className="vote-split" aria-label="Vote split" style={{ height: 6 }} />;
}
